import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { ApiError, type AdminApi } from "@/api";
import type { TcLoopProgress } from "@/types";
import { Badge, Banner, Button, Textarea } from "@/components/ui";

const STATE_LABEL: Record<string, string> = { unresolved: "미해결", resolved: "해결", deferred: "보류" };

/**
 * The cause loop's state of one TC, in its drawer (LOOPFLOW E-14): 미해결 /
 * 해결 / 보류, the hypotheses tried out of 10, and a person's 보류 or 다시 시작
 * with a reason. The server computes the state from the attempts and the
 * latest person's change: 다시 시작 counts again from that moment.
 */
export function CauseLoopSummary({ api, testCaseId }: { api: AdminApi; testCaseId: string }) {
  const [progress, setProgress] = useState<TcLoopProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setProgress((await api.getTcLoopProgress(testCaseId)).progress);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? "이 TC의 루프 상태를 볼 수 없습니다." : message(e));
    }
  }, [api, testCaseId]);

  useEffect(() => {
    setProgress(null);
    void load();
  }, [load]);

  async function change(state: "deferred" | "unresolved") {
    if (!progress) return;
    setSaving(true);
    setError(null);
    try {
      const out = await api.setTcLoopState(testCaseId, { state, reason: reason.trim() });
      setProgress(out.progress);
      setReason("");
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? "이 TC의 루프 상태를 볼 수 없습니다." : message(e));
    } finally {
      setSaving(false);
    }
  }

  if (!progress) {
    return (
      <div className="text-[12px] text-muted-foreground" aria-label="루프 상태">
        {error ? <Banner tone="error">{error}</Banner> : <Loader2 className="inline size-3.5 animate-spin" />}
      </div>
    );
  }
  const target = progress.state === "unresolved" ? "deferred" : "unresolved";
  return (
    <div className="grid gap-2 text-[12.5px]" aria-label="루프 상태">
      <div className="flex flex-wrap items-center gap-2">
        <Badge size="compact" className="border-border">
          {STATE_LABEL[progress.state] ?? progress.state}
        </Badge>
        <span className="tabular-nums">
          시도 {progress.attempts_counted}/{progress.attempt_limit}
        </span>
        {progress.failures > 0 && <span className="text-muted-foreground">실패 {progress.failures}</span>}
        {progress.common === "not_run" && <span className="text-muted-foreground">common 미실행</span>}
      </div>
      {progress.state === "resolved" && (
        <p className="text-muted-foreground">
          적용 대기에 들어가 해결됨(실제 적용 전), common 품질 시험은 아직 실행 안 함.
        </p>
      )}
      {progress.reason && (
        <p className="text-muted-foreground">
          {progress.changed_kind === "human" ? `${progress.changed_by ?? "사람"}: ` : ""}
          {progress.reason}
        </p>
      )}
      {error && <Banner tone="error">{error}</Banner>}
      <Textarea
        aria-label="상태를 바꾸는 사유"
        placeholder="사유 (필수)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
      />
      <div>
        <Button size="sm" variant="outline" disabled={saving || !reason.trim()} onClick={() => void change(target)}>
          {saving && <Loader2 className="animate-spin" />}
          {target === "deferred" ? "보류로 바꾸기" : "다시 시작(미해결로)"}
        </Button>
      </div>
    </div>
  );
}

function message(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}
